import { supabase } from '../client';
import type { Requisition, RequisitionClientEngagement } from '@core/types';
import { requisitionHistoryService } from './requisitionHistoryService';
import { requisitionService } from './requisitionService';

function plainToHtml(text: string): string {
  const esc = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${esc.split(/\r?\n\r?\n/).join('</p><p>').replace(/\n/g, '<br/>')}</p>`;
}

export function resolveHiringClientEmail(req: Requisition): string {
  const hm = req.hiring_manager_email?.trim();
  if (hm) return hm;
  const c = req.client?.contact_email?.trim();
  if (c) return c;
  return '';
}

export const requisitionClientGateService = {
  async listEngagements(requisitionId: string): Promise<RequisitionClientEngagement[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_requisition_client_engagements')
      .select('*')
      .eq('requisition_id', requisitionId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as RequisitionClientEngagement[];
  },

  /** Queue email to hiring client and move req to awaiting_client. */
  async sendOutreachEmail(opts: {
    req: Requisition;
    orgId: string;
    toAddress: string;
    subject: string;
    bodyPlain: string;
    actorName: string;
    actorOrgUserId: string | null;
    /** When true, only resend email (stay in awaiting_client). */
    resend?: boolean;
  }): Promise<Requisition> {
    const { req, orgId, toAddress, subject, bodyPlain, actorName, actorOrgUserId, resend } = opts;
    const { data: org } = await supabase
      .from('ggj_b2b_organizations')
      .select('billing_email')
      .eq('id', orgId)
      .single();
    const fromAddress = (org?.billing_email as string | null)?.trim() || 'noreply@gograbjob.com';

    const { error: qErr } = await supabase.from('ggj_b2b_email_send_queue').insert({
      org_id: orgId,
      template_key: 'requisition_client_outreach',
      from_address: fromAddress,
      to_addresses: [toAddress],
      cc_addresses: [],
      subject: subject.trim().slice(0, 500),
      body_html: plainToHtml(bodyPlain),
      merge_vars: {
        requisition_id: req.id,
        requisition_title: req.title,
        client_name: req.client?.name ?? '',
      },
      status: 'pending',
      created_by: actorName,
    });
    if (qErr) throw qErr;

    const now = new Date().toISOString();
    const { error: eErr } = await supabase.from('ggj_b2b_requisition_client_engagements').insert({
      org_id: orgId,
      requisition_id: req.id,
      kind: 'email_to_client',
      channel: 'email',
      to_address: toAddress,
      subject: subject.trim().slice(0, 500),
      body: bodyPlain.trim(),
      created_by: actorName,
    });
    if (eErr) throw eErr;

    const fromStatus = req.status;
    const nextStatus = resend ? 'awaiting_client' : ('awaiting_client' as const);
    const patch: Partial<Requisition> = {
      status: nextStatus,
      client_outreach_sent_at: now,
    };
    const updated = await requisitionService.update(req.id, patch);
    if (!resend || fromStatus !== 'awaiting_client') {
      await requisitionHistoryService.record(orgId, req.id, fromStatus, nextStatus, {
        changedBy: actorName,
        changedById: actorOrgUserId,
        note: resend ? 'Resent hiring-client requisition email' : 'Sent hiring-client requisition email',
      });
    }
    return updated;
  },

  /** Log what the client said without advancing workflow. */
  async logClientReply(opts: {
    orgId: string;
    requisitionId: string;
    body: string;
    actorName: string;
  }): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_requisition_client_engagements').insert({
      org_id: opts.orgId,
      requisition_id: opts.requisitionId,
      kind: 'client_reply_recorded',
      channel: 'email',
      to_address: null,
      subject: null,
      body: opts.body.trim(),
      created_by: opts.actorName,
    });
    if (error) throw error;
  },

  /** After client confirms — move to submitted so internal approval + notify can run. */
  async recordClientConfirmation(opts: {
    req: Requisition;
    orgId: string;
    summaryPlain: string;
    actorName: string;
    actorOrgUserId: string | null;
  }): Promise<Requisition> {
    const { req, orgId, summaryPlain, actorName, actorOrgUserId } = opts;
    if (req.status !== 'awaiting_client') {
      throw new Error('Client confirmation is only recorded while status is Awaiting client.');
    }
    const { error } = await supabase.from('ggj_b2b_requisition_client_engagements').insert({
      org_id: orgId,
      requisition_id: req.id,
      kind: 'client_confirmation',
      channel: 'email',
      to_address: null,
      subject: 'Client confirmation (recorded)',
      body: summaryPlain.trim(),
      created_by: actorName,
    });
    if (error) throw error;

    const now = new Date().toISOString();
    const updated = await requisitionService.update(req.id, {
      status: 'submitted',
      submitted_at: req.submitted_at ?? now,
    });
    await requisitionHistoryService.record(orgId, req.id, 'awaiting_client', 'submitted', {
      changedBy: actorName,
      changedById: actorOrgUserId,
      note: 'Hiring client confirmed — ready for internal approval',
    });
    return updated;
  },

  /** Emergency: skip hiring-client gate (audited). */
  async skipClientGate(opts: {
    req: Requisition;
    orgId: string;
    actorName: string;
    actorOrgUserId: string | null;
    reason?: string;
  }): Promise<Requisition> {
    const { req, orgId, actorName, actorOrgUserId, reason } = opts;
    if (req.status !== 'draft') {
      throw new Error('Skip client gate is only available from Draft.');
    }
    const now = new Date().toISOString();
    const updated = await requisitionService.update(req.id, {
      status: 'submitted',
      submitted_at: now,
    });
    await requisitionHistoryService.record(orgId, req.id, 'draft', 'submitted', {
      changedBy: actorName,
      changedById: actorOrgUserId,
      note: reason?.trim() || 'Skipped hiring-client notification (override)',
    });
    return updated;
  },
};
